import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ITMS } from "@/constants/testIds";
import { useDepots, fmtDepot } from "@/lib/depots";
import { toast } from "sonner";
import { Users, ShieldCheck, GearSix, BookOpen, Plus, Trash, Lock, Bell, Plug, Database, CreditCard } from "@phosphor-icons/react";

const TABS = [
  { k: "users", l: "User Management", i: Users },
  { k: "roles", l: "Roles & Permissions", i: ShieldCheck },
  { k: "modules", l: "Module Access", i: GearSix },
  { k: "fleet", l: "Fleet Settings", i: GearSix },
  { k: "route", l: "Route Settings", i: GearSix },
  { k: "fare", l: "Fare Settings", i: GearSix },
  { k: "duty", l: "Duty Rules", i: GearSix },
  { k: "passcaps", l: "Pass Trip Caps", i: GearSix },
  { k: "cardcfg", l: "Transit Card · Fee & Deposit", i: CreditCard },
  { k: "notif", l: "Notifications", i: Bell },
  { k: "integ", l: "Integrations", i: Plug },
  { k: "sec", l: "Security", i: Lock },
  { k: "audit", l: "Audit Logs", i: BookOpen },
  { k: "sys", l: "System Config", i: Database },
];

export default function AdminSettings() {
  const depotMap = useDepots();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState({ roles: [], modules: [], actions: [] });
  const [logs, setLogs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    employee_id: "", name: "", email: "", phone: "",
    department: "Operations", depot: "Panaji", role: "Read Only User", password: "password123",
  });

  const load = async () => {
    const [u, r, l] = await Promise.all([
      api.get("/admin/users"), api.get("/admin/roles"), api.get("/admin/audit-logs"),
    ]);
    setUsers(u.data); setRoles(r.data); setLogs(l.data);
  };
  useEffect(() => { load(); }, []);

  const addUser = async () => {
    try {
      const { data } = await api.post("/admin/users", form);
      setUsers((p) => [data, ...p]);
      toast.success(`Added ${data.name}`);
      setShowAdd(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delUser = async (id) => {
    await api.delete(`/admin/users/${id}`);
    setUsers((p) => p.filter(u => u.id !== id));
    toast.success("User removed");
  };

  return (
    <div className="flex flex-col lg:flex-row h-full min-w-0">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 border-r border-slate-200 bg-white shrink-0">
        <div className="p-4 border-b border-slate-200">
          <div className="label-cap text-slate-500">Administration</div>
          <h2 className="font-display font-bold text-lg">Settings</h2>
        </div>
        <nav data-testid={ITMS.adminTabs} className="p-2">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors text-left
                ${tab===t.k?"bg-[#EFF6FF] text-[#1E3A8A] font-semibold":"text-slate-600 hover:bg-slate-50"}`}>
              <t.i size={16} weight="duotone"/>{t.l}
            </button>
          ))}
        </nav>
      </aside>

      {/* Mobile/tablet horizontal tab strip */}
      <div className="lg:hidden bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="px-4 pt-3 pb-1">
          <div className="label-cap text-slate-500">Administration</div>
          <h2 className="font-display font-bold text-lg">Settings</h2>
        </div>
        <nav data-testid={`${ITMS.adminTabs}-mobile`} className="flex gap-1 overflow-x-auto scroll-thin px-2 pb-2">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors whitespace-nowrap
                ${tab===t.k?"bg-[#EFF6FF] text-[#1E3A8A] font-semibold":"text-slate-600 hover:bg-slate-50 border border-slate-200"}`}>
              <t.i size={14} weight="duotone"/>{t.l}
            </button>
          ))}
        </nav>
      </div>

      <section className="flex-1 overflow-auto scroll-thin p-4 lg:p-6 min-w-0">
        {tab === "users" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="label-cap text-slate-500">Identity & Access</div>
                <h1 className="font-display text-xl lg:text-2xl font-bold">User Management</h1>
              </div>
              <button data-testid={ITMS.addUserButton} onClick={() => setShowAdd(true)}
                className="ktcl-btn-primary rounded-md px-4 py-2 text-sm font-semibold flex items-center gap-2">
                <Plus size={14}/> Add User
              </button>
            </div>

            {showAdd && (
              <div className="ktcl-card p-4">
                <h3 className="font-display font-bold mb-3">New User</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {["employee_id","name","email","phone"].map(k => (
                    <Field key={k} label={k.replace("_"," ")}>
                      <input value={form[k]} onChange={(e)=>setForm({...form, [k]: e.target.value})}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/>
                    </Field>
                  ))}
                  <Field label="Role">
                    <select value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">
                      {roles.roles.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </Field>
                  <Field label="Depot">
                    <input value={form.depot} onChange={(e)=>setForm({...form, depot: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/>
                  </Field>
                  <Field label="Password">
                    <input type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/>
                  </Field>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={addUser} className="ktcl-btn-primary rounded-md px-4 py-2 text-sm font-semibold">Create User</button>
                  <button onClick={()=>setShowAdd(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
                </div>
              </div>
            )}

            <div className="ktcl-card overflow-auto scroll-thin">
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr className="text-left text-xs text-slate-500">
                  {"Employee ID,Name,Email,Department,Depot,Role,MFA,Status,Last Login,Action".split(",").map(h => (
                    <th key={h} className="px-3 py-2 font-medium">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono text-xs">{u.employee_id}</td>
                      <td className="px-3 py-2 font-semibold">{u.name}</td>
                      <td className="px-3 py-2 text-xs">{u.email}</td>
                      <td className="px-3 py-2 text-xs">{u.department}</td>
                      <td className="px-3 py-2 text-xs">{fmtDepot(u.depot, depotMap)}</td>
                      <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">{u.role}</span></td>
                      <td className="px-3 py-2 text-xs">{u.mfa_status}</td>
                      <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">{u.status}</span></td>
                      <td className="px-3 py-2 text-[10px] text-slate-500">{u.last_login ? new Date(u.last_login).toLocaleString() : "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={()=>delUser(u.id)} className="text-slate-400 hover:text-[#EF4444]"><Trash size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "roles" && (
          <div className="space-y-4">
            <h1 className="font-display text-2xl font-bold">Roles & Permissions Matrix</h1>
            <div className="ktcl-card overflow-auto scroll-thin">
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr className="text-left text-xs text-slate-500">
                  <th className="px-3 py-2 font-medium sticky left-0 bg-slate-50">Role</th>
                  {roles.actions.map(a => <th key={a} className="px-3 py-2 font-medium text-center">{a}</th>)}
                </tr></thead>
                <tbody>
                  {roles.roles.map(r => (
                    <tr key={r} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-semibold sticky left-0 bg-white">{r}</td>
                      {roles.actions.map(a => (
                        <td key={a} className="px-3 py-2 text-center">
                          <input type="checkbox" defaultChecked={r==="Super Admin" || (r!=="Read Only User" && a==="View")}
                            className="accent-[#1E3A8A]"/>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "modules" && (
          <div className="space-y-4">
            <h1 className="font-display text-2xl font-bold">Module Access</h1>
            <div className="ktcl-card overflow-auto scroll-thin">
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr className="text-left text-xs text-slate-500">
                  <th className="px-3 py-2 font-medium">Module</th>
                  {roles.roles.slice(0, 6).map(r => <th key={r} className="px-3 py-2 font-medium text-center text-[10px]">{r}</th>)}
                </tr></thead>
                <tbody>
                  {roles.modules.map(m => (
                    <tr key={m} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-semibold">{m}</td>
                      {roles.roles.slice(0, 6).map(r => (
                        <td key={r} className="px-3 py-2 text-center">
                          <input type="checkbox" defaultChecked={r==="Super Admin" || r==="Operations Manager"} className="accent-[#1E3A8A]"/>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "audit" && (
          <div className="space-y-4">
            <h1 className="font-display text-2xl font-bold">Audit Logs</h1>
            <div className="ktcl-card overflow-auto scroll-thin">
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr className="text-left text-xs text-slate-500">
                  {"Timestamp,User,Action,Details,IP".split(",").map(h => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-xs font-mono">{new Date(l.timestamp).toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs">{l.user}</td>
                      <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-slate-100 rounded">{l.action}</span></td>
                      <td className="px-3 py-2 text-xs text-slate-600">{l.details}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{l.ip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "sec" && <PlaceholderPanel title="Security Settings" items={[
          "MFA enforcement","Password policy (min 12 chars, complexity)","IP allow-list","Session timeout · 30 min idle",
          "Geo restrictions · India only","Device trust","Failed login lockout · 5 attempts","Single sign-on (SAML/OIDC)",
          "Audit logging","Encryption at rest","Daily snapshots","Disaster recovery RPO 1h, RTO 4h",
        ]}/>}
        {tab === "notif" && <PlaceholderPanel title="Notifications" items={["Breakdown SMS","Battery low push","Crew violation email","Daily depot summary","Weekly KPI digest"]}/>}
        {tab === "integ" && <PlaceholderPanel title="Integrations" items={["AIS-140 GPS","Rugtek RP-6S ETM","Smart Card EMV","UPI ticketing","SMS gateway","Email/SMTP","Webhook outbox","Power BI export"]}/>}
        {tab === "fleet" && <PlaceholderPanel title="Fleet Settings" items={["Vehicle type catalog","Service intervals","Tyre rotation policy","Insurance providers","Permit categories"]}/>}
        {tab === "route" && <PlaceholderPanel title="Route Settings" items={["Master Stop Registry · 1,284 stops","Route Geometry · GeoJSON","Interchange Rules","Distance Matrix","Travel Time Matrix"]}/>}
        {tab === "fare" && <PlaceholderPanel title="Fare Settings" items={["Stage Fare Matrix","Pass Products","Student concession 50%","Senior citizen 35%","Govt staff 25%","Event fare overrides"]}/>}
        {tab === "duty" && <PlaceholderPanel title="Duty Rules" items={["Max 8h driving / 12h spread","Mandatory 30-min break per 5h","Weekly cap 48h","Night shift premium","Overtime trigger"]}/>}
        {tab === "passcaps" && <TripCapsPanel/>}
        {tab === "cardcfg" && <TransitCardConfigPanel/>}
        {tab === "sys" && <PlaceholderPanel title="System Configuration" items={["Database: MongoDB","API base: /api","Time zone: IST (UTC+05:30)","Retention: 5 years","Backups: nightly · 21:00 IST"]}/>}
      </section>
    </div>
  );
}

function TripCapsPanel() {
  const [cfg, setCfg] = useState({ caps: {}, disability_unlimited: true });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/config/trip-caps").then(({data}) => setCfg(data)).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config/trip-caps", { caps: cfg.caps, disability_unlimited: cfg.disability_unlimited });
      toast.success("Pass trip caps saved");
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };
  const setCap = (k, v) => setCfg({ ...cfg, caps: { ...cfg.caps, [k]: Math.max(0, parseInt(v) || 0) }});
  return (
    <div className="space-y-4" data-testid="trip-caps-panel">
      <div>
        <h1 className="font-display text-2xl font-bold">Pass Trip Caps</h1>
        <p className="text-sm text-slate-600 mt-1">Daily trip limits per pass category. <b>0 = unlimited</b>. Used by Smart Pass holders in the MajheApp customer webapp.</p>
      </div>
      <div className="ktcl-card p-4 space-y-3">
        {Object.entries(cfg.caps || {}).map(([cat, cap]) => (
          <div key={cat} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
            <div className="flex-1">
              <div className="font-semibold text-sm">{cat}</div>
              <div className="text-[11px] text-slate-500">
                {cap === 0 ? "Unlimited trips per day" : `${cap} trip${cap !== 1 ? "s" : ""} per day`}
              </div>
            </div>
            <input type="number" min={0} max={50}
              data-testid={`cap-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-center font-bold outline-none focus:border-[#1E3A8A]"
              value={cap} onChange={(e) => setCap(cat, e.target.value)}/>
          </div>
        ))}
      </div>
      <div className="ktcl-card p-4 flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm">Disability Pass: always unlimited</div>
          <div className="text-[11px] text-slate-500">Override category cap for &ldquo;Person with Disability&rdquo; passes to grant 100% / unlimited trips.</div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" data-testid="disability-unlimited-toggle"
            className="sr-only peer" checked={!!cfg.disability_unlimited}
            onChange={(e) => setCfg({ ...cfg, disability_unlimited: e.target.checked })}/>
          <span className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-[#5B2EE5] transition-colors"></span>
          <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5 shadow"/>
        </label>
      </div>
      <button data-testid="trip-caps-save" onClick={save} disabled={saving}
        className="ktcl-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold">
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><div className="text-xs font-medium text-slate-600 mb-1 capitalize">{label}</div>{children}</div>
);

function TransitCardConfigPanel() {
  const [cfg, setCfg] = useState({ deposit_amount: 0, deposit_enabled: false, card_fee: 150 });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/config/transit-card").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config/transit-card", {
        deposit_amount: Number(cfg.deposit_amount) || 0,
        deposit_enabled: !!cfg.deposit_enabled,
        card_fee: Number(cfg.card_fee) || 150,
      });
      toast.success("Smart Transit Card configuration saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const totalAtIssuance = (Number(cfg.card_fee) || 0) + (cfg.deposit_enabled ? (Number(cfg.deposit_amount) || 0) : 0);

  return (
    <div className="space-y-4" data-testid="transit-card-config-panel">
      <div>
        <h1 className="font-display text-2xl font-bold">Smart Transit Card · Fee &amp; Deposit</h1>
        <p className="text-sm text-slate-600 mt-1">
          The registration fee is paid by the customer and credited back in FULL as wallet balance.
          The optional refundable deposit (off by default) lets KTCL hold a security amount that is
          returned when the card is cancelled. Edit the values below and click <b>Save</b> — changes
          apply instantly to both the admin Issue Card flow and the MajheApp customer apply flow.
        </p>
      </div>

      {/* Card Fee */}
      <div className="ktcl-card p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="font-semibold text-sm">Registration Fee · always credited back as balance</div>
            <div className="text-[11px] text-slate-500">Customer pays this and walks away with an equal amount as wallet balance.</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 text-sm font-bold">₹</span>
            <input type="number" min={0} max={1000} step={10}
              data-testid="card-fee-input"
              className="w-28 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-center font-bold outline-none focus:border-[#1E3A8A]"
              value={cfg.card_fee ?? 150}
              onChange={(e) => setCfg({ ...cfg, card_fee: e.target.value })}/>
          </div>
        </div>
      </div>

      {/* Deposit toggle + value */}
      <div className="ktcl-card p-4 space-y-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="font-semibold text-sm">Refundable Deposit · activation toggle</div>
            <div className="text-[11px] text-slate-500">When ON, KTCL holds the deposit. Refunded in full when card is cancelled.</div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox"
              data-testid="deposit-enabled-toggle"
              className="sr-only peer" checked={!!cfg.deposit_enabled}
              onChange={(e) => setCfg({ ...cfg, deposit_enabled: e.target.checked })}/>
            <span className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-[#5B2EE5] transition-colors"></span>
            <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5 shadow"/>
          </label>
        </div>
        <div className={`flex items-center justify-between gap-4 ${cfg.deposit_enabled ? "" : "opacity-50 pointer-events-none"}`}>
          <div className="flex-1">
            <div className="font-semibold text-sm">Deposit Amount</div>
            <div className="text-[11px] text-slate-500">Editable — set ₹0 to skip, up to ₹5,000.</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 text-sm font-bold">₹</span>
            <input type="number" min={0} max={5000} step={50}
              data-testid="deposit-amount-input"
              className="w-28 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-center font-bold outline-none focus:border-[#1E3A8A]"
              value={cfg.deposit_amount ?? 0}
              onChange={(e) => setCfg({ ...cfg, deposit_amount: e.target.value })}/>
          </div>
        </div>
      </div>

      {/* Computed total */}
      <div className="ktcl-card p-4 bg-gradient-to-br from-violet-50 to-indigo-50 border-violet-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-violet-700 font-bold">Customer pays at issuance</div>
            <div className="text-[11px] text-slate-600 mt-0.5">
              Fee (₹{cfg.card_fee || 0}) {cfg.deposit_enabled && <>+ Deposit (₹{cfg.deposit_amount || 0})</>} + any extra top-up
            </div>
          </div>
          <div className="text-3xl font-display font-bold text-[#1E3A8A]" data-testid="total-at-issuance">
            ₹{totalAtIssuance}
          </div>
        </div>
        <div className="text-[11px] text-violet-700 mt-2 italic">
          ✓ Wallet balance on card: ₹{cfg.card_fee || 0} {cfg.deposit_enabled && <>· Deposit held separately: ₹{cfg.deposit_amount || 0}</>}
        </div>
      </div>

      <button data-testid="card-cfg-save" onClick={save} disabled={saving}
        className="ktcl-btn-primary rounded-md px-5 py-2.5 text-sm font-semibold">
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

const PlaceholderPanel = ({ title, items }) => (
  <div className="space-y-4">
    <h1 className="font-display text-2xl font-bold">{title}</h1>
    <div className="ktcl-card p-4 space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
          <div className="text-sm font-medium">{it}</div>
          <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">Configured</span>
        </div>
      ))}
    </div>
  </div>
);
